import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/layout/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import CalculatorPage from './pages/CalculatorPage';
import { UsersPage, CurrenciesPage, InsurancePage, CountryConfigPage, CustomBurdensPage, OrganisationsPage } from './pages/AdminPages';
import CalculationsHistoryPage from './pages/CalculationsHistoryPage';
import ComparisonPage from './pages/ComparisonPage';
import PricingPage from './pages/PricingPage';
import PlansPage from './pages/PlansPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import SubscriptionPage from './pages/SubscriptionPage';

// ── Guards ────────────────────────────────────────────────────────────────────
const Guard = ({ children, adminOnly, feature }) => {
  const { user, isAdmin, hasFeature } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/" replace />;
  if (feature && !hasFeature(feature)) return <Navigate to="/" replace />;
  return children;
};

// ── Main routes ───────────────────────────────────────────────────────────────
function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      {/* Public */}
      <Route path="/login"  element={<LoginPage />} />

      {/* Authenticated — all plans */}
      <Route path="/" element={<Guard><Layout><DashboardPage /></Layout></Guard>} />
      <Route path="/calculator" element={<Guard><Layout><CalculatorPage /></Layout></Guard>} />
      <Route path="/calculations" element={<Guard><Layout><CalculationsHistoryPage /></Layout></Guard>} />

      {/* Authenticated — Standard plan and above only */}
      <Route path="/comparison" element={<Guard feature="comparison"><Layout><ComparisonPage /></Layout></Guard>} />

      {/* Org subscription page — only users with an org */}
      <Route path="/subscription"         element={<Guard><Layout><SubscriptionPage /></Layout></Guard>} />
      <Route path="/subscription/success" element={<Guard><SubscriptionSuccessPage /></Guard>} />
      <Route path="/plans"                element={<Guard><Layout><PlansPage /></Layout></Guard>} />

      {/* Internal Quoteology admin only */}
      <Route path="/admin/pricing"      element={<Guard adminOnly><Layout><PricingPage /></Layout></Guard>} />
      <Route path="/admin/orgs"         element={<Guard adminOnly><Layout><OrganisationsPage /></Layout></Guard>} />
      <Route path="/admin/users"        element={<Guard adminOnly><Layout><UsersPage /></Layout></Guard>} />
      <Route path="/admin/currencies"   element={<Guard adminOnly><Layout><CurrenciesPage /></Layout></Guard>} />
      <Route path="/admin/insurance"    element={<Guard adminOnly><Layout><InsurancePage /></Layout></Guard>} />
      <Route path="/admin/country-config" element={<Guard adminOnly><Layout><CountryConfigPage /></Layout></Guard>} />
      <Route path="/admin/custom-burdens" element={<Guard adminOnly><Layout><CustomBurdensPage /></Layout></Guard>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" toastOptions={{ duration: 3500 }} />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}